import * as THREE from 'three';
import { CONFIG } from '../config.js';
import { TestArena } from './TestArena.js';
import { AtmosphereEffects } from './effects/AtmosphereEffects.js';
import { FlyingShipFleet } from './effects/FlyingShipFleet.js';
import { RoadVehicleTraffic } from './effects/RoadVehicleTraffic.js';
import { InteriorManager } from './interiors/InteriorManager.js';

/**
 * Container for environment geometry and lighting.
 */
export class World {
  /** @param {{ config: typeof CONFIG.world }} options */
  constructor({ config }) {
    this.config = config;
    this.root = new THREE.Group();
    this.root.name = 'WorldRoot';

    this._arena = new TestArena();
    this._atmosphere = new AtmosphereEffects();
    this._flyingShips = new FlyingShipFleet();
    this._roadVehicles = new RoadVehicleTraffic();
    this._interiors = new InteriorManager();
    this._lights = this._createLighting();
    /** @type {THREE.Scene | null} */
    this._scene = null;
    /** @type {THREE.AmbientLight | null} */
    this._ambient = null;
    /** @type {THREE.Fog | THREE.FogExp2 | null} */
    this._fog = null;
    this._baseFogNear = CONFIG.graphics.fogNear;
    this._baseFogFar = CONFIG.streaming.renderDistance;
  }

  /**
   * @param {{ x: number; z: number }} [nearPosition] When set, only nearby chunk colliders (streaming).
   * @returns {import('./Colliders.js').BoxCollider[]}
   */
  getColliders(nearPosition) {
    if (this._interiors.isInside) {
      return this._interiors.getColliders(nearPosition);
    }
    return this._arena.getColliders(nearPosition);
  }

  getInteriorManager() {
    return this._interiors;
  }

  getEntranceRegistry() {
    return this._arena.getEntranceRegistry();
  }

  /** Colliders for the streamed city (ignores active interior volume). */
  getExteriorColliders(nearPosition) {
    return this._arena.getColliders(nearPosition);
  }

  getSpawnPoint() {
    return this._arena.getSpawnPoint();
  }

  getNavigationGraph() {
    return this._arena.getNavigationGraph();
  }

  /** @returns {import('./streaming/ChunkManager.js').ChunkManager | null} */
  getChunkManager() {
    return this._arena.getChunkManager();
  }

  getAtmosphereEffects() {
    return this._atmosphere;
  }

  getFlyingShipFleet() {
    return this._flyingShips;
  }

  getRoadVehicleTraffic() {
    return this._roadVehicles;
  }

  attachToScene(scene) {
    this._scene = scene;
    scene.background = new THREE.Color(this.config.backgroundColor);

    const arena = this._arena.build();
    this.root.add(
      ...this._lights,
      arena,
      this._atmosphere.group,
      this._flyingShips.group,
      this._roadVehicles.group,
    );
    scene.add(this.root);
    this._interiors.attachToWorld(this.root);
  }

  /**
   * @param {import('../rendering/GraphicsSettings.js').GraphicsSettings} settings
   */
  applyGraphicsSettings(settings) {
    if (!this._scene) return;

    const fogColor = CONFIG.graphics.fogColor;
    const near = this._baseFogNear;
    const far = settings.renderDistance ?? this._baseFogFar;
    this._baseFogFar = far;

    if (settings.fog) {
      if (CONFIG.graphics.fogExp2) {
        if (!(this._scene.fog instanceof THREE.FogExp2)) {
          this._scene.fog = new THREE.FogExp2(fogColor, CONFIG.graphics.fogDensity);
        } else {
          this._scene.fog.color.setHex(fogColor);
          this._scene.fog.density = CONFIG.graphics.fogDensity;
        }
        this._fog = this._scene.fog;
      } else if (!this._scene.fog || this._scene.fog instanceof THREE.FogExp2) {
        this._scene.fog = new THREE.Fog(fogColor, near, far);
        this._fog = this._scene.fog;
      } else {
        this._scene.fog.color.setHex(fogColor);
        this._scene.fog.near = near;
        this._scene.fog.far = far;
        this._fog = this._scene.fog;
      }
    } else {
      this._scene.fog = null;
      this._fog = null;
    }

    if (this._ambient) {
      this._ambient.intensity = settings.fog
        ? CONFIG.graphics.ambientIntensity
        : 0.2;
    }
  }

  /**
   * @param {number} delta
   * @param {{ x: number; y: number; z: number }} playerPosition
   */
  update(delta, playerPosition) {
    const streamPos = this._interiors.getStreamingPosition(playerPosition);
    this._arena.update(delta, streamPos);
    this._atmosphere.update(delta, streamPos);
    this._flyingShips.update(delta, streamPos);
    this._roadVehicles.update(delta, streamPos);
    this._interiors.updateMaterials(delta);
    this._applyHeightFog(playerPosition.y);
  }

  /** Clearer horizon when the player gains elevation (sense of scale). */
  _applyHeightFog(playerY) {
    if (!this._fog || !CONFIG.graphics.fog) return;
    const lift = Math.max(0, playerY - CONFIG.atmosphere.heightFogStart);
    const boost = lift * CONFIG.atmosphere.heightFogFarBoost;

    if (this._fog instanceof THREE.FogExp2) {
      this._fog.density = Math.max(
        0.004,
        CONFIG.graphics.fogDensity - lift * 0.00035,
      );
    } else if (this._fog instanceof THREE.Fog) {
      this._fog.far = this._baseFogFar + boost;
      this._fog.near = this._baseFogNear + lift * 0.35;
    }
  }

  getStreamingStats() {
    return this._arena.getStreamingStats();
  }

  dispose() {
    this._arena.dispose();
    this._atmosphere.dispose();
    this._flyingShips.dispose();
    this._roadVehicles.dispose();
    this._interiors.dispose();
    this.root.clear();
  }

  _createLighting() {
    const ambient = new THREE.AmbientLight(
      CONFIG.matrix.primaryGreen,
      CONFIG.graphics.ambientIntensity * 0.45,
    );
    this._ambient = ambient;

    const accentAmbient = new THREE.AmbientLight(
      CONFIG.matrix.primaryRed,
      CONFIG.graphics.ambientIntensity * 0.55,
    );

    const hemi = new THREE.HemisphereLight(
      CONFIG.matrix.primaryGreen,
      0x000804,
      0.42,
    );

    const key = new THREE.DirectionalLight(CONFIG.matrix.primaryRed, 0.62);
    key.position.set(18, 32, 12);

    const greenRim = new THREE.DirectionalLight(CONFIG.matrix.primaryGreen, 0.28);
    greenRim.position.set(-24, 16, -18);

    const fill = new THREE.PointLight(CONFIG.matrix.primaryRed, 14, 52);
    fill.position.set(-10, 7, 10);

    const streetGlow = new THREE.PointLight(CONFIG.matrix.primaryGreen, 9, 38);
    streetGlow.position.set(12, 3, -8);

    return [ambient, accentAmbient, hemi, key, greenRim, fill, streetGlow];
  }
}
